import { env } from 'node:process';
import { pathExists } from '@wix/cli-fs';
import { join } from 'node:path';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { matcher } from 'variant';
import { NPM } from './adapters/npm.js';
import { Yarn, resolveYarnVersion } from './adapters/yarn.js';
import type { IPackageManager, PackageManagerName } from './types.js';
import { RepoType } from './repo-type.js';

export async function createPackageManager(
  repoType: RepoType
): Promise<IPackageManager> {
  const packageManagerName = await getPackageManagerName(repoType);

  switch (packageManagerName) {
    case 'yarn-berry':
      return new Yarn(repoType, 'yarn-berry');
    case 'yarn':
      return new Yarn(repoType, 'yarn');
    case 'npm':
      return new NPM(repoType);
  }
}

async function getPackageManagerName(
  repoType: RepoType
): Promise<PackageManagerName> {
  // In case of an existing repo we first check for relevant package manager files.
  // this is an extra layer of identification that should prevent errors when the
  // user runs create-app using a package manager other than the one used in the project

  const getByEnv = (): PackageManagerName => {
    if (env.npm_config_user_agent?.startsWith('yarn/1')) {
      return 'yarn';
    }

    // yarn v2+
    if (env.npm_config_user_agent?.startsWith('yarn')) {
      return 'yarn-berry';
    }

    if (env.npm_config_user_agent?.startsWith('npm')) {
      return 'npm';
    }

    throw new CliError({
      code: CliErrorCode.UnsupportedPackageManager({
        packageManagerName: env.npm_config_user_agent,
      }),
      cause: null,
    });
  };

  return matcher(repoType)
    .when(RepoType.None, getByEnv)
    .when(
      [RepoType.Polyrepo, RepoType.Monorepo],
      async ({ rootDir }): Promise<PackageManagerName> => {
        const [hasYarnRc, hasYarnLock, hasPackageLock] = await Promise.all([
          pathExists(join(rootDir, '.yarnrc.yml')),
          pathExists(join(rootDir, 'yarn.lock')),
          pathExists(join(rootDir, 'package-lock.json')),
        ]);

        if (hasYarnRc || hasYarnLock) {
          const yarnVersion = await resolveYarnVersion(rootDir);
          return yarnVersion.startsWith('1') ? 'yarn' : 'yarn-berry';
        }

        if (hasPackageLock) {
          return 'npm';
        }

        return getByEnv();
      }
    )
    .complete();
}
